/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash/get';
import {
    Spinner,
    SecurityHelper,
    Heading,
    FlexRow,
    FlexCol,
    NeedToKnow,
} from 'sarsaparilla';
import { withCompatRouter, COMPAT_ROUTER_PROP_TYPES } from 'sarsaparilla/legacy';

import { updateAccount } from 'ui-account/src/actions/profileActions';
import { authModalActions } from 'shared-ui/auth/redux';
import AlternateHolderSideDetail from '../components/altPermitHolderConfirmationPage/AlternateHolderSideDetail';
import ErrorNotification from '../components/ErrorNotification';
import * as types from '../constants';
import * as pActions from '../actions/permit';
import GroupDetails from '../components/altPermitHolderConfirmationPage/GroupDetails';
import { getDivisionName, isMobile, scrollUp } from '../utils/utilsNew';

export class AlternatePermitHolderConfirmationPage extends Component {
    static propTypes = {
        alertId: PropTypes.string,
        account: PropTypes.object,
        confirmAlternateError: PropTypes.object,
        declineAlternateError: PropTypes.object,
        dispatch: PropTypes.func,
        issuance: PropTypes.object,
        permit: PropTypes.object,
        recarea: PropTypes.array,
        router: COMPAT_ROUTER_PROP_TYPES,
        verifyAlternateError: PropTypes.object,
        fetchIssuanceError: PropTypes.object,
    };

    constructor(props) {
        super(props);
        this.state = {
            account: {
                cell_phone: '',
                email: '',
                first_name: '',
                home_address: {
                    address1: '',
                    address2: '',
                    city: '',
                    state: '',
                    zip_code: '',
                },
                last_name: '',
                user_id: '',
            },
            errors: [],
            needToKnowChecked: false,
            saveLeader: false,
        };
    }

    componentDidMount() {
        const loggedin = SecurityHelper.isLoggedIn();
        if (!loggedin) {
            this.props.dispatch(authModalActions.openLoginModal());
        } else {
            this.fetchData();
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.account && !prevProps.account) {
            this.fetchData();
        }
    }

    componentWillUnmount() {
        this.props.dispatch(pActions.clearError());
        this.props.dispatch(pActions.clearFetchIssuanceError());
        this.props.dispatch(pActions.clearFetchError());
    }

    fetchData() {
        const reservationId = this.props.router.params.reservationId;
        const issuanceId = reservationId.split(types.RES_ID_DELIMITER)[0];
        const permitId = reservationId.split(types.RES_ID_DELIMITER)[1];

        this.props.dispatch(pActions.verifyAlternate(permitId, issuanceId)).then(() => {
            this.props.dispatch(pActions.fetchPermitReservationAlert(reservationId));
        });

        this.props.dispatch(pActions.fetchPermit(permitId)).then(() => {
            let recId = 0;
            if (get(this.props, 'permit.recareas[0]')) {
                recId = this.props.permit.recareas[0];
                this.props.dispatch(pActions.fetchRecarea(recId));
            }
            this.props.dispatch(pActions.fetchIssuance(issuanceId, permitId));
            if (this.props.account) {
                this.setState({ account: this.props.account.account });
            }
        });
    }

    acceptAlternateRequest = () => {
        if (!this.isThisFormValid()) {
            scrollUp();
            return;
        }
        if (this.state.saveLeader) {
            this.saveLeaderInfo();
        }

        const { permit_id: permitId, id } = this.props.issuance;
        if (permitId && id && this.props.alertId) {
            // If the props issuance permit id and issuance id were successfully fetched, use those values to confirm the alternate request
            this.props.dispatch(pActions.confirmAlternate(permitId, id)).then(() => {
                if (!this.props.confirmAlternateError) {
                    pActions.markAlertAsRead(this.props.alertId);
                    window.location = '/account/reservations';
                }
            });
        }
    };

    declineAlternateRequest = () => {
        const { permit_id: permitId, id } = this.props.issuance;
        if (permitId && id && this.props.alertId) {
            // If the props issuance permit id and issuance id were successfully fetched, use those values to confirm the alternate request
            this.props.dispatch(pActions.declineAlternate(permitId, id)).then(() => {
                if (!this.props.declineAlternateError) {
                    pActions.markAlertAsRead(this.props.alertId);
                    window.location = '/account/reservations';
                }
            });
        }
    };

    handleGroupDetailChange = (groupDetail) => {
        this.setState({ account: groupDetail.groupLeader });
    };

    handleSaveLeaderChange = (saveLeader) => {
        this.setState({ saveLeader });
    };

    handleNeedToKnowCheckboxChange = () => {
        this.setState(
            (prevState) => ({ needToKnowChecked: !prevState.needToKnowChecked }),
            () => {
                if (this.state.needToKnowChecked) {
                    this.isThisFormValid();
                    if (!isMobile.any()) {
                        scrollUp();
                    }
                }
            }
        );
    };

    fetchPermitHolder() {
        /* eslint-disable camelcase */
        // returns an object with keys 'permit_holder' and 'permit_holder_address'
        // for use when sending issuance object away
        const {
            first_name,
            last_name,
            email,
            user_id,
            cell_phone: phone,
            home_address: homeAddress,
        } = this.state.account;
        const {
            address1: street_address1,
            address2: street_address2,
            city,
            state: state_code,
            zip_code: postal_code,
        } = homeAddress;

        return {
            permit_holder: { first_name, last_name, email, user_id, phone },
            permit_holder_address: {
                street_address1,
                street_address2,
                city,
                state_code,
                postal_code,
            },
        };
    }

    permitHolderValidation = () => {
        const INVALID = {
            PERMIT_HOLDER_FIELDS_MISSING:
                'Please fill out all required Permit Holder fields.',
        };
        const { permit_holder: ph, permit_holder_address: pha } =
            this.fetchPermitHolder();
        if (!(ph.first_name && ph.last_name && ph.email && ph.phone)) return INVALID;
        if (!(pha.street_address1 && pha.city && pha.state_code && pha.postal_code)) {
            return INVALID;
        }
        return {};
    };

    isThisFormValid = () => {
        const validation = {
            ...this.permitHolderValidation(),
            ...(this.state.needToKnowChecked
                ? {}
                : { NEED_CHECK: 'Need to Know must be acknowledged' }),
        };
        const errors = Object.values(validation);
        this.setState({ errors });
        return errors.length === 0;
    };

    createUpdatedAccountObject(prevAcct) {
        // eslint-disable-next-line
        const { first_name, last_name, home_address, email, cell_phone } =
            this.state.account;
        return { ...prevAcct, first_name, last_name, home_address, email, cell_phone };
    }

    isLottery() {
        return this.props.issuance && !!this.props.issuance.lottery_id;
    }

    saveLeaderInfo() {
        const acct = this.createUpdatedAccountObject(SecurityHelper.getAccount());
        this.props.dispatch(updateAccount(acct));
    }

    render() {
        if (!this.props.permit) {
            return (
                <div className="permit-loading-wrapper">
                    <Spinner />
                </div>
            );
        }

        if (!this.props.permit.rules) {
            return <div />;
            // return an error here?
        }
        const {
            confirmAlternateError,
            declineAlternateError,
            verifyAlternateError,
            fetchIssuanceError,
        } = this.props;
        const permitHolder = get(this.props.issuance, 'permit_holder', null);
        const requester = permitHolder
            ? `${permitHolder.first_name} ${permitHolder.last_name}`
            : 'someone';

        const groupDetail = {
            groupLeaderType: '',
            groupLeader: this.state.account,
            otherLeader: {
                fName: '',
                mName: '',
                lName: '',
                phone: '',
                email: '',
            },
            altLeaders: [{ email: '' }],
        };

        const displayErrors = [...this.state.errors];
        if (!displayErrors.length) {
            displayErrors.push(
                confirmAlternateError,
                declineAlternateError,
                verifyAlternateError,
                fetchIssuanceError
            );
        }

        return (
            <div className="rec-order-detail-page per-registration-page">
                <div className="rec-two-col-outer-wrap">
                    <div className="rec-two-col-wrap">
                        <FlexRow>
                            <FlexCol lg={8}>
                                <Heading
                                    headingLevel={1}
                                    appearance="h1"
                                    className="rec-order-detail-page-title per-alt-holder-page-title"
                                >
                                    Alternate Permit Holder Confirmation
                                </Heading>
                                <ErrorNotification
                                    errorHeader="Error:"
                                    errors={displayErrors}
                                />
                                {verifyAlternateError ? null : (
                                    <div>
                                        <div className="rec-order-detail-section">
                                            <div className="rec-order-detail-section-header">
                                                <div className="rec-order-detail-section-title">
                                                    {this.props.permit.name}
                                                </div>
                                            </div>
                                            <div className="rec-order-detail-section-body">
                                                <div className="rec-sr-only">
                                                    My Account
                                                </div>
                                                <div className="rec-order-detail-subsection-title">
                                                    You have been asked by{' '}
                                                    <span className="brand-text-green">
                                                        {requester}
                                                    </span>{' '}
                                                    to be an alternate permit holder.
                                                </div>
                                                <GroupDetails
                                                    conditionalComponents={{
                                                        ...this.props.permit.components,
                                                        alternate_group_leader: false,
                                                    }}
                                                    groupDetail={groupDetail}
                                                    handleGroupDetailChange={
                                                        this.handleGroupDetailChange
                                                    }
                                                    handleSaveLeaderChange={
                                                        this.handleSaveLeaderChange
                                                    }
                                                    validatingAlternateHolder
                                                />
                                                <div className="per-alt-permit-holder-form per-section-group">
                                                    <div className="rec-order-detail-subsection-title">
                                                        Alternate Permit Holders
                                                    </div>
                                                    <div className="per-subsection-title-secondary">
                                                        Why an Alternate?
                                                    </div>
                                                    <p>
                                                        The permit holder is required to
                                                        be present on the entire trip for
                                                        this permit to be valid. In the
                                                        event that the permit holder is
                                                        unavailable, alternate permit
                                                        holder can be designated at the
                                                        time of the reservation.
                                                    </p>
                                                    <div className="per-subsection-title-secondary">
                                                        How to Add an Alternate
                                                    </div>
                                                    <p>
                                                        Enter an email address for an
                                                        alternate permit holder. The
                                                        alternate permit holder must
                                                        respond and accept being an
                                                        alternate within{' '}
                                                        <strong>72 hours</strong> or this
                                                        permit will not have an alternate.
                                                        If the alternate does not have a{' '}
                                                        {process.env.SITE_NAME} account,
                                                        they will be required to create an
                                                        account.
                                                    </p>
                                                    <div className="per-subsection-title-secondary">
                                                        Alternate Permit Holder
                                                        Considerations
                                                    </div>
                                                    <p>
                                                        Being an alternate carries the
                                                        same responsibilities as the
                                                        primary permit holder. If there
                                                        are seasonal reservations
                                                        restrictions, these will apply to
                                                        the alternate as if they are the
                                                        primary permit holder. In other
                                                        words, an alternate may not be
                                                        able to reserve another permit or
                                                        be an alternate for another permit
                                                        at this facility for this season.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <NeedToKnow
                                            siteName={this.props.permit.name}
                                            siteInfo={get(
                                                this.props.permit,
                                                'description.need_to_know_order'
                                            )}
                                            isChecked={this.state.needToKnowChecked}
                                            onChange={this.handleNeedToKnowCheckboxChange}
                                        />
                                    </div>
                                )}
                            </FlexCol>
                            {verifyAlternateError ? null : (
                                <FlexCol lg={4}>
                                    <AlternateHolderSideDetail
                                        permit={this.props.permit}
                                        issuance={this.props.issuance}
                                        divisionName={getDivisionName(
                                            this.props.permit,
                                            this.props.issuance
                                        )}
                                        recarea={this.props.recarea}
                                        entryDate={this.props.issuance.start_date}
                                        exitDate={this.props.issuance.end_date}
                                        isLottery={this.isLottery()}
                                        buttonDisabled={!get(this.props.issuance, 'id')}
                                        acceptAlternateRequest={
                                            this.acceptAlternateRequest
                                        }
                                        declineAlternateRequest={
                                            this.declineAlternateRequest
                                        }
                                    />
                                </FlexCol>
                            )}
                        </FlexRow>
                    </div>
                </div>
            </div>
        );
    }
}

function select(state) {
    return {
        alertId: get(state.permit, 'reservation_alert.id'),
        permit: state.permit.result,
        issuance: state.permit.issuance,
        recarea: state.permit.recarea,
        fetchIssuanceError: state.permit.fetch_issuance_error,
        verifyAlternateSuccess: state.permit.verify_alternate_success,
        verifyAlternateError: state.permit.verify_alternate_error,
        confirmAlternateSuccess: state.permit.confirm_alternate_success,
        confirmAlternateError: state.permit.confirm_alternate_error,
        declineAlternateSuccess: state.permit.decline_alternate_success,
        declineAlternateError: state.permit.decline_alternate_error,
        account: state.auth.account,
    };
}

export default withCompatRouter(connect(select)(AlternatePermitHolderConfirmationPage));
